import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './DashboardCards.module.scss';
import { useUserContext } from '../../../../context/UserContext';
import SharingModalContent from '../../../../components/_stateful/SharingModal/SharingModalContent';
import SharingModal from '../../../../components/_stateful/SharingModal';
import Toast from '../../../../../common/utils/Toast';
import VideoService from '../../../../service/VideoService';
import ListHeader from '../ListHeader/ListHeader';
import { useLibraryContext } from '../../../../context/LibraryContext';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import CardsList from '../../../../components/CardList/CardList';

import MoveModal from '../../../../components/MoveModal/MoveModal';
import MoveModalContent from '../../../../components/MoveModalContent/MoveModalContent';
import ConfirmMoveModalContent from '../../../../components/ConfirmMoveModalContent/ConfirmMoveModalContent';
import { useTranslation } from 'react-i18next';
import DeleteSharedVideoModal from '../../../../components/DeleteSharedVideoModal/DeleteSharedVideoModal';
import RecordNotFound from '../../../../components/_empty_state/RecordNotFound';
import { LoadingSpinner } from '../../../../components/Icon/icons';
import cx from 'classnames';
import Col from '../../../../components/GridSystem/Col';
import Ref from '../../../../components/Ref/Ref';
import Row from '../../../../components/GridSystem/Row';
import VideosFilter from '../VideosFilter/VideosFilter';
import { approvalStatusesForFilter } from '../VideosFilter/constants';

import DownloadModal from '../../../../components/_stateful/DownloadModal';
import DownloadModalContent from '../../../../components/_stateful/DownloadModal/DownloadModalContent';
import { ToastNotification } from '../../../../components/Toast/Toast';

const shareModalInit = {
  open: false,
  id: '',
  videoid: '',
  thumbnailURL: '',
  url: '',
  trackerURL: '',
};

const downloadModalInit = {
  open: false,
  story: null,
};

const videoToDeleteInit = {
  id: '',
  videoIndex: -1,
  name,
  author: '',
};

const videoToMoveInit = {
  videoId: '',
  videoName: '',
  videoIndex: -1,
  folder: '',
  author: {},
  isProtected: false,
};

function DashboardCards(props) {
  const {
    searchInput,
    videos,
    listMode = false,
    loadVideos,
    loadFolders,
    setVideosAndTotalData,
    folder = {},
    isUserHasEditingRights,
    isUserHasAdvancedEditingRights,
    archived,
    hideActions = false,
    onDuplicateSucceeded = () => { },
    selectable = true,
    folderVideos = false,
    query = {},
    loading,
    isVideosEmpty,
    infiniteRef,
    selectedFilter,
    handleChangeApprovalFilter,
    isApprovalFlowEnabled,
    isWebActivated,
    totalData,
    colClassnames,
    videoTabHeader = true,
    alt = '',
  } = props;
  const { user, logFirebaseEvent, appConfig } = useUserContext();

  const [shareModal, setShareModal] = useState(shareModalInit);
  const [downloadModal, setDownloadModal] = useState(downloadModalInit);
  const [moveModal, setMoveModal] = useState(false);
  const [confirmMoveModal, setConfirmMoveModal] = useState(false);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(videoToDeleteInit);
  const [videoToMove, setVideoToMove] = useState(videoToMoveInit);

  const [renameVideoData, setRenameVideoData] = useState({});
  const [showMenu, setShowMenu] = useState({});
  const [story, setStory] = useState(downloadModalInit.story);

  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const [squeezeCard, setSqueezeCard] = useState(false);
  const { t } = useTranslation();

  const menuRef = useRef(null);
  useOutsideClick(menuRef, () => {
    if (Object.keys(showMenu).length) {
      setShowMenu({});
    }
  });

  const {
    selectedCard,
    toggleSelectedCard,
    toggleSelectedAllCard,
    isShareShow,
    toggleShareShow,
    isDownloadVideos,
    toggleDownloadSelectedVideos,
    isDeleteVideos,
    toggleDeleteVideos,
    toggleUnselectCards,
    isArchiveVideos,
    toggleArchiveVideos,
    toggleRenameVideo,
    isRenameVideo,

    toggleDuplicateVideo,
    isDuplicateVideo,

    isMoveVideos,
    toggleMoveVideos,
    selectedFolder,
    folderToMove,
    updateFolderToMove,
    isRemoveFromReviewVideo,
    toggleRemoveFromReviewVideo,
  } = useLibraryContext();

  useEffect(() => {
    setSqueezeCard(Object.keys(selectedCard).length + Object.keys(selectedFolder).length);
  }, [selectedCard, selectedFolder]);

  const shareVideo = useCallback(async id => {
    try {
      setShareModal({ open: true, videoid: id });
    } catch (e) {
      Toast.error(e);
    }
  }, []);

  const handleMoveVideo = useCallback(async (id, name, videoIndex, currentFolderId, author, isProtected) => {
    setMoveModal(true);
    if (id !== '') {
      setVideoToMove({
        id,
        name,
        videoIndex,
        folder: currentFolderId,
        author,
        isProtected,
      });
    }
  }, []);

  const downloadVideo = useCallback(
    async storyId => {
      // If video doesn't need processing, use old download method to download immediately instead of showing modal first
      let videoDetails = {};
      if (folder) {
        videoDetails = await VideoService.getVideoDetails(storyId);
      } else {
        videoDetails = await VideoService.getVideoDetails(storyId);
      }

      setStory(videoDetails);
      setDownloadModal({ open: true, video: videoDetails });
    },
    [appConfig]
  );

  const duplicateVideo = useCallback(
    async (id, name) => {
      const loadingToast = ToastNotification({
        message: `${t('general.toast.duplicating')}`,
        isMobileView: isMobileView,
        spinner: true,
        autoClose: false,
      });

      try {
        const response = await VideoService.duplicateVideo(id);
        if (!response) {
          throw `Unexpected response: ${response}`;
        }
        logFirebaseEvent('cm_dup_cl', { story_id: id });

        if (folder?.id && isUserHasAdvancedEditingRights) {
          await VideoService.updateVideo(response.id, { folder: folder.id });
        }

        document.getElementById('cardsHeaderID')?.scrollIntoView();
        await loadVideos();
        toast.dismiss(loadingToast);
        ToastNotification({
          message: `${t('general.toast.duplicating.successfully')} '${name}'`,
          iconName: 'success',
          isMobileView: isMobileView,
        });

        if (!(folder?.id && isUserHasAdvancedEditingRights) && onDuplicateSucceeded) {
          onDuplicateSucceeded();
        }
      } catch (e) {
        toast.dismiss(loadingToast);
        Toast.error('Error duplicating video');
      }
    },
    [folder, isUserHasAdvancedEditingRights]
  );

  const renameVideoAction = useCallback(
    async id => {
      if (!renameVideoData[id].error) {
        try {
          await VideoService.updateVideo(id, { name: renameVideoData[id].name });
          setRenameVideoData({});
          await loadVideos();
        } catch (e) {
          console.log('Error while renaming', e);
          setRenameVideoData({});
        }
      } else {
        setRenameVideoData({});
      }
    },
    [renameVideoData]
  );

  const renameVideo = useCallback(
    uuid => {
      setRenameVideoData({
        [uuid]: {
          name: videos.find(({ id }) => id === uuid).name,
        },
      });
    },
    [setRenameVideoData, videos]
  );

  const handleDotsClick = id => {
    setShowMenu({
      uuid: id,
      flag: true,
    });
  };

  const archiveVideos = useCallback(
    async (uuid, name) => {
      try {
        if (!uuid && Object.keys(selectedCard).length) {
          const uuids = Object.keys(selectedCard);
          Promise.all(
            uuids.map(uuid => {
              return VideoService.updateVideo(uuid, { archived: 1 });
            })
          ).then(() => {
            loadVideos();
            toggleUnselectCards();
            ToastNotification({
              message: `${t('general.toast.archive_successfully_videos')}`,
              iconName: 'archive',
              isMobileView: isMobileView,
            });
          });
        } else if (uuid) {
          await VideoService.updateVideo(uuid, { archived: 1 });
          await loadVideos();
          ToastNotification({
            message: `${t('general.toast.archive_successfully')} '${name}'`,
            iconName: 'archive',
            isMobileView: isMobileView,
          });
        }
      } catch (e) {
        Toast.error(t('general.toast.unable_to_archive'), e);
      }
    },
    [selectedCard, setVideosAndTotalData]
  );

  const showConfirmationModal = useCallback((id, videoIndex, name, author) => {
    setConfirming(true);
    if (id !== '') {
      setVideoToDelete({
        id,
        videoIndex,
        name,
        author,
      });
    }
  }, []);

  useEffect(() => {
    if (confirming) {
      const isVideoOwner = videoToDelete?.author?.username === user.username;
      if (!isVideoOwner) {
        setConfirmDeleteModal(true);
        return;
      }
      deleteVideoForever();
    }
  }, [videoToDelete]);

  const deleteVideoForever = useCallback(
    async (isSkipConfirm = false) => {
      const confirmDelete =
        isSkipConfirm ||
        confirm(
          `Are you sure you want to permanently delete ${videoToDelete.name ? `"${videoToDelete.name}"` : 'the selected videos'
          }? This can’t be undone.`
        );
      console.log('confirmDelete', confirmDelete);
      if (confirmDelete) {
        try {
          const { id, videoIndex, name } = videoToDelete;
          if (videoIndex === -1) {
            const uuids = Object.keys(selectedCard);
            Promise.all(
              uuids.map(id => {
                return VideoService.deleteVideoForever(id);
              })
            ).then(() => {
              ToastNotification({
                message: `${t('general.toast.delete_successfully_videos')}`,
                iconName: 'success',
                isMobileView: isMobileView,
              });
              setConfirming(false);
              setVideoToDelete(videoToDeleteInit);
              loadVideos();
              toggleUnselectCards();
            });
          } else {
            await VideoService.deleteVideoForever(id);
            ToastNotification({
              message: `${t('general.toast.delete_successfully')} '${name}'`,
              iconName: 'success',
              isMobileView: isMobileView,
            });
            loadVideos();
            setConfirming(false);
            setVideoToDelete(videoToDeleteInit);
            toggleUnselectCards();
          }
        } catch (e) {
          Toast.error(e);
        }
      } else {
        setConfirming(false);
        setVideoToDelete(videoToDeleteInit);
      }
    },
    [videoToDelete, showConfirmationModal, selectedCard, setVideosAndTotalData]
  );

  const onRenameInputChange = (e, id) => {
    setRenameVideoData({
      [id]: {
        name: e.target.value.substring(0, 100),
        error: e.target.value.trim().length === 0,
      },
    });
  };

  const downloadVideos = async () => {
    const uuidArr = Object.values(selectedCard);
    try {
      toggleDownloadSelectedVideos();
      await Promise.all(
        uuidArr.map(async ({ id }) => {
          downloadVideo(id);
        })
      );
    } catch (e) {
      console.log('Multiple videos download error: ', e);
    }
  };

  const moveVideos = async () => {
    if (folder?.users?.length) {
      setMoveModal(false);
      setConfirmMoveModal(true);
      return;
    }

    try {
      const { id, name, videoIndex } = videoToMove;
      if (folderToMove.id === undefined) return;
      if (videoIndex === -1) {
        const uuids = Object.keys(selectedCard);
        await VideoService.batchUpdateVideos({ stories: uuids, folderId: folderToMove.id });
        setMoveModal(false);
        setVideoToMove(videoToMoveInit);
        loadVideos();
        toggleUnselectCards();
        updateFolderToMove({});
        toggleMoveVideos();
        ToastNotification({
          message: `Videos ${t('general.toast.succesfully_moved_plural')} '${folderToMove.name}'`,
          iconName: 'success',
          isMobileView: isMobileView,
        });
        loadFolders && (await loadFolders());
        await loadVideos();
      } else {
        if (Object.keys(selectedCard).find(item => item === id)) toggleSelectedCard({ id });

        await VideoService.updateVideo(id, { folder: folderToMove.id });

        updateFolderToMove({});
        ToastNotification({
          message: `${name} ${t('general.toast.succesfully_moved')} '${folderToMove.name}'`,
          iconName: 'success',
          isMobileView: isMobileView,
        });
        setMoveModal(false);
        setVideoToMove(videoToMoveInit);
        loadFolders && (await loadFolders());
        await loadVideos();
      }
    } catch (e) {
      Toast.error(e);
    }
  };

  const confirmMoveVideos = async () => {
    try {
      const { id, name, videoIndex, author } = videoToMove;
      if (folderToMove.id === undefined) return;

      if (videoIndex === -1) {
        const uuids = Object.values(selectedCard);
        await Promise.all(
          uuids.map(({ id, author }) => {
            const isVideoOwner = author?.username === user.username;
            if (isVideoOwner) {
              return VideoService.updateVideo(id, { folder: folderToMove.id });
            }

            return VideoService.claimVideo(id, { folder: folderToMove.id });
          })
        );

        setConfirmMoveModal(false);
        setVideoToMove(videoToMoveInit);
        loadVideos();
        toggleUnselectCards();
        updateFolderToMove({});
        toggleMoveVideos();
        ToastNotification({
          message: `Videos ${t('general.toast.succesfully_moved_plural')} '${folderToMove.name}'`,
          iconName: 'success',
          isMobileView: isMobileView,
        });
        loadFolders && (await loadFolders());
        await loadVideos();
      } else {
        if (Object.keys(selectedCard).find(item => item === id)) toggleSelectedCard({ id });

        const isVideoOwner = author?.username === user.username;
        if (isVideoOwner) {
          await VideoService.updateVideo(id, { folder: folderToMove.id });
        } else {
          await VideoService.claimVideo(id, { folder: folderToMove.id });
        }

        updateFolderToMove({});
        ToastNotification({
          message: `${name} ${t('general.toast.succesfully_moved')} '${folderToMove.name}'`,
          iconName: 'success',
          isMobileView: isMobileView,
        });
        setConfirmMoveModal(false);
        setVideoToMove(videoToMoveInit);
        loadFolders && (await loadFolders());
        await loadVideos();
      }
    } catch (e) {
      setConfirmMoveModal(false);
      Toast.error(e);
    }
  };

  const removeFromReviewVideo = useCallback(
    async (uuid, name) => {
      try {
        const uuids = Object.keys(selectedCard);

        if (uuid) {
          await VideoService.updateVideo(uuid, { approvalStatus: 1 });
          ToastNotification({
            message: `${name} ${t('general.toast.remove_from_review.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();

          return;
        }

        if (uuids.length) {
          await Promise.all(uuids.map(uuid => VideoService.updateVideo(uuid, { approvalStatus: 1 })));
          ToastNotification({
            message: `${t('general.toast.remove_from_review.success')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
        }
      } catch (e) {
        Toast.error(t('general.toast.remove_from_review.error'), e);
      }
    },
    [selectedCard]
  );

  useEffect(() => {
    const removeFromReview = async () => {
      await removeFromReviewVideo();
      toggleRemoveFromReviewVideo();
      toggleUnselectCards();
    };

    if (selectable && isRemoveFromReviewVideo) {
      removeFromReview();
    }
  }, [isRemoveFromReviewVideo]);

  useEffect(() => {
    return () => toggleUnselectCards();
  }, []);

  useEffect(() => {
    if (selectable && isShareShow) {
      const uuid = Object.keys(selectedCard)[0];
      shareVideo(uuid);
    }
  }, [isShareShow]);

  useEffect(() => {
    if (selectable && isDownloadVideos) {
      downloadVideos();
    }
  }, [isDownloadVideos]);

  useEffect(() => {
    if (selectable && isMoveVideos) {
      if (!moveModal) {
        setMoveModal(true);
      }
    }
  }, [isMoveVideos]);

  const handleCloseMoveModal = () => {
    toggleUnselectCards();
    updateFolderToMove({});
    isMoveVideos && toggleMoveVideos();
    setVideoToMove(videoToMoveInit);
    setMoveModal(false);
  };

  const handleCloseConfirmMoveModal = () => {
    toggleUnselectCards();
    updateFolderToMove({});
    isMoveVideos && toggleMoveVideos();
    setVideoToMove(videoToMoveInit);
    setConfirmMoveModal(false);
  };

  useEffect(() => {
    const deleteVideos = async () => {
      setConfirming(true);
      toggleDeleteVideos();

      const isNotVideosOwner =
        !!Object.values(selectedCard).length && Object.values(selectedCard).every(({ author }) => author.username !== user.username);
      if (isNotVideosOwner) {
        setConfirmDeleteModal(true);
        return;
      }
      await deleteVideoForever();
    };

    if (selectable && isDeleteVideos) {
      deleteVideos();
    }
  }, [isDeleteVideos]);

  useEffect(() => {
    const archive = async () => {
      await archiveVideos();
      toggleArchiveVideos();
    };
    if (selectable && isArchiveVideos) {
      archive();
    }
  }, [isArchiveVideos]);

  useEffect(() => {
    if (selectable && isRenameVideo) {
      const uuid = Object.keys(selectedCard)[0];
      renameVideo(uuid);
      toggleRenameVideo();
      toggleUnselectCards();
    }
  }, [isRenameVideo]);

  useEffect(() => {
    if (selectable && isDuplicateVideo) {
      const uuid = Object.keys(selectedCard)[0];
      const vDetails = Object.values(selectedCard)[0];
      duplicateVideo(vDetails.id, vDetails.name);
      toggleDuplicateVideo();
      toggleUnselectCards();
    }
  }, [isDuplicateVideo]);

  const handleCopyClick = async id => {
    logFirebaseEvent('copy_link_cl', { story_id: id });
    navigator.clipboard.writeText(VideoService.getShareURL(id));
    ToastNotification({
      message: t('general.toast.link_copied'),
      iconName: 'success',
      isMobileView: isMobileView,
    });
  };

  const unarchiveVideos = useCallback(
    async (uuid, videoIndex, name) => {
      try {
        const uuids = Object.keys(selectedCard);
        if (!uuid && uuids.length) {
          await Promise.all(uuids.map(uuid => VideoService.updateVideo(uuid, { archived: 0 })));
          ToastNotification({
            message: `${t('general.toast.unarchive_successfully_videos')}`,
            iconName: 'unarchive',
            isMobileView: isMobileView,
          });
          loadVideos();
          toggleUnarchive();
          toggleUnselectCards();
        } else if (uuid) {
          await VideoService.updateVideo(uuid, { archived: 0 });
          setVideosAndTotalData(prevVideos => {
            const temp = [...prevVideos];
            temp.splice(videoIndex, 1);
            return temp;
          });
          ToastNotification({
            message: `${t('general.toast.unarchive_successfully')} '${name}'`,
            iconName: 'unarchive',
            isMobileView: isMobileView,
          });
        }
      } catch (e) {
        // Toast.error('Unable to restore this video.');
      }
    },
    [setVideosAndTotalData, selectedCard]
  );

  const handleCardClick = (event, videoIndex) => {
    const parentElement = event.target.parentElement.parentElement;
    if (
      parentElement.tagName !== 'BUTTON' &&
      parentElement.tagName !== 'LI' &&
      parentElement.tagName !== 'UL' &&
      event.target.tagName !== 'LI' &&
      event.target.tagName !== 'UL'
    ) {
      toggleSelectedCard(videos[videoIndex]);
    }
  };

  const handleAllCardClick = () => {
    toggleSelectedAllCard(videos);
  };

  const closeShareModal = () => {
    setShareModal(shareModalInit);

    if (isShareShow) {
      toggleShareShow();
    }
  };

  return (
    <>
      {videoTabHeader && (
        <div className={styles.videoTabHeader}>
          {loading || (folderVideos && !folder) ? (
            <h2>{t('general.loading')}</h2>
          ) : (
            !(query?.searchTerm && videos?.length === 0 && !isVideosEmpty) && (
              <>
                {folderVideos ? (
                  <h2 tabIndex={0} id="cardsHeaderID">{`${t('folder.videos.title')} (${videos && totalData})`}</h2>
                ) : (
                  <h2 tabIndex={0} id="cardsHeaderID">
                    {' '}
                    {t('dashboard.all_videos')} ({videos && totalData})
                  </h2>
                )}
                {isApprovalFlowEnabled && isWebActivated && (
                  <VideosFilter selectedItem={selectedFilter} data={approvalStatusesForFilter} onClick={handleChangeApprovalFilter} />
                )}
              </>
            )
          )}
        </div>
      )}
      <Col xs={12} className={cx(styles.videoLibrary, colClassnames)}>
        <Ref innerRef={infiniteRef}>
          <Row className={styles.row}>
            {(!loading || videos.length > 0) ? (
              <>
                {listMode && videos.length > 0 ? (
                  <ListHeader
                    isAllCardSelected={Object.keys(selectedCard).length === videos.length}
                    handleAllCardClick={handleAllCardClick}
                  />
                ) : null}
                {videos ? (
                  <CardsList
                    menuRef={menuRef}
                    listMode={listMode}
                    videos={videos}
                    folder={folder}
                    hideActions={hideActions}
                    isUserHasEditingRights={isUserHasEditingRights}
                    isUserHasAdvancedEditingRights={isUserHasAdvancedEditingRights}
                    searchInput={searchInput}
                    toggleShowMenu={() => setShowMenu({})}
                    renameVideoData={renameVideoData}
                    renameVideo={archived ? undefined : renameVideo}
                    handleCopyClick={archived ? undefined : handleCopyClick}
                    shareVideo={archived ? undefined : shareVideo}
                    removeFromReview={archived ? undefined : removeFromReviewVideo}
                    duplicateVideo={archived ? undefined : duplicateVideo}
                    downloadVideo={archived ? undefined : downloadVideo}
                    archiveVideos={archived ? undefined : archiveVideos}
                    handleDotsClick={handleDotsClick}
                    showMenu={showMenu}
                    onRenameInputChange={onRenameInputChange}
                    handleCardClick={handleCardClick}
                    deleteVideo={showConfirmationModal}
                    renameVideoAction={renameVideoAction}
                    moveVideoAction={archived ? undefined : handleMoveVideo}
                    unarchiveVideo={archived ? unarchiveVideos : undefined}
                    isMobileView={isMobileView}
                    squeezeCard={squeezeCard}
                    isShowApprovalStatus
                    alt={alt}
                  />
                ) : null}
                <SharingModal open={shareModal.open} onClose={closeShareModal}>
                  <div className={styles.contentContainer}>
                    <SharingModalContent
                      onClose={closeShareModal}
                      videoId={shareModal.videoid}
                      anonUrl={shareModal.url}
                      anonThumbnailUrl={shareModal.thumbnailURL}
                      anonTrackerUrl={shareModal.trackerURL}
                      user={user}
                    />
                  </div>
                </SharingModal>
                <DownloadModal open={downloadModal.open} onClose={() => setDownloadModal(downloadModalInit)}>
                  <div className={styles.contentContainer}>
                    <DownloadModalContent
                      onClose={() => setDownloadModal(downloadModalInit)}
                      video={story}
                      setVideo={setStory}
                      user={user}
                    />
                  </div>
                </DownloadModal>
                <MoveModal open={moveModal} onClose={handleCloseMoveModal}>
                  <MoveModalContent
                    handleMoving={moveVideos}
                    userId={user.username}
                    onClose={handleCloseMoveModal}
                    video={videoToMove}
                    selectedVideos={selectedCard}
                    archived={archived}
                    isMobileView={isMobileView}
                  />
                </MoveModal>
                <MoveModal open={confirmMoveModal} onClose={handleCloseConfirmMoveModal}>
                  <ConfirmMoveModalContent
                    confirmMove={confirmMoveVideos}
                    contributorsNumber={folder.users?.length}
                    onClose={handleCloseConfirmMoveModal}
                    isMultipleVideos={Object.keys(selectedCard)?.length > 1}
                  />
                </MoveModal>
                <DeleteSharedVideoModal
                  video={videoToDelete}
                  videos={Object.values(selectedCard)}
                  isShow={confirmDeleteModal}
                  confirmDeletion={deleteVideoForever}
                  onClose={() => setConfirmDeleteModal(false)}
                />
              </>
            ) : null}
            {loading && videos.length > 0 ? (
              <div className={styles.spinnerContainer}>
                <LoadingSpinner theme="light" />
              </div>
            ) : null}
          </Row>
        </Ref>
      </Col>
      {!loading && (query?.searchTerm || query?.tags?.length > 0) && videos?.length === 0 && !isVideosEmpty ? <RecordNotFound searchTerm={query.searchTerm} /> : null}
    </>
  );
}

export default DashboardCards;
